<template>
  <div class="track-page-layout" :class="{ 'track-page-layout--no-map': !linkedTrack }">
    <aside class="track-sidebar">
      <div class="track-sidebar-scroll">
        <div v-if="loading" class="track-sidebar-content">
          <AppBreadcrumbs :items="breadcrumbItems" />
          <h1 class="track-title">{{ trip?.name || 'Поездка' }}</h1>
          <p class="empty-state">Загрузка...</p>
        </div>

        <div v-else-if="error" class="track-sidebar-content">
          <AppBreadcrumbs :items="breadcrumbItems" />
          <h1 class="track-title">{{ trip?.name || 'Поездка' }}</h1>
          <p class="error-message">{{ error }}</p>
        </div>

        <div v-else-if="trip" class="track-sidebar-content">
          <AppBreadcrumbs :items="breadcrumbItems" />
          <h1 class="track-title">{{ trip.name }}</h1>
          <p class="track-item-meta">{{ formatTripDate(trip.trip_date) }}</p>
          <p class="track-item-meta">{{ visibilityLabel(trip.public) }}</p>

          <fieldset v-if="isOwner" class="visibility-field">
            <legend class="track-item-meta">Видимость</legend>
            <label class="radio-label">
              <input
                type="radio"
                :checked="!trip.public"
                :disabled="updatingVisibilitySettings"
                @change="toggleVisibility(false)"
              />
              Приватная
            </label>
            <label class="radio-label">
              <input
                type="radio"
                :checked="trip.public"
                :disabled="updatingVisibilitySettings"
                @change="toggleVisibility(true)"
              />
              Публичная
            </label>
          </fieldset>

          <div v-if="trip.author && !isOwner && auth.isAuthenticated" class="author-row">
            <AuthorLink :author="trip.author" prefix="Автор:" size="sm" class="track-item-meta" />
            <FavoriteButton
              label="Автор в избранное"
              :favorited="authorFavorited"
              :disabled="favoritingAuthor"
              @favorite="favoriteAuthor"
              @unfavorite="unfavoriteAuthor"
            />
          </div>

          <div v-else-if="trip.author" class="track-item-meta track-item-author">
            <AuthorLink :author="trip.author" prefix="Автор:" size="sm" />
          </div>

          <div v-if="trip.companions?.length" class="trip-companions">
            <h3>Спутники</h3>
            <div class="companion-chips">
              <AuthorLink
                v-for="companion in trip.companions"
                :key="companion.id"
                :author="companion"
                size="sm"
              />
            </div>
          </div>

          <div v-if="trip.track" class="linked-track">
            <h3>Трек</h3>
            <router-link :to="{ name: 'track', params: { id: trip.track.id } }">
              {{ trip.track.name }}
            </router-link>
            <p class="track-item-meta">
              {{ formatDistance(trip.track.distance_m) }} · {{ formatDuration(trip.track.duration_s) }}
            </p>
          </div>

          <div v-if="sanitizedBody" class="trip-body" v-html="sanitizedBody" />

          <TripGallery :photos="trip.photos || []" />

          <div v-if="auth.isAuthenticated" class="track-actions">
            <ReactionBar
              :likes-count="trip.likes_count ?? 0"
              :dislikes-count="trip.dislikes_count ?? 0"
              :user-reaction="trip.user_reaction"
              :disabled="reactingTrip"
              @react="reactToTrip"
              @unreact="unreactToTrip"
            />
            <span class="track-stats">{{ trip.comments_count ?? 0 }} 💬</span>
            <FavoriteButton
              label="Поездка в избранное"
              :favorited="trip.favorited"
              :disabled="favoritingTrip"
              @favorite="favoriteCurrentTrip"
              @unfavorite="unfavoriteCurrentTrip"
            />
          </div>

          <div v-else class="track-actions track-actions--readonly">
            <span>{{ trip.likes_count ?? 0 }} 👍</span>
            <span>{{ trip.dislikes_count ?? 0 }} 👎</span>
            <span>{{ trip.comments_count ?? 0 }} 💬</span>
          </div>

          <GuestAuthPrompt v-if="!auth.isAuthenticated" />

          <div v-if="isOwner" class="btn-row" style="margin-top: 1rem">
            <router-link class="btn btn-secondary" :to="{ name: 'trip-edit', params: { id: trip.id } }">
              Редактировать
            </router-link>
            <button class="btn btn-danger" :disabled="deleting" @click="removeTrip">
              {{ deleting ? 'Удаление...' : 'Удалить' }}
            </button>
          </div>

          <section class="comments-section panel">
            <h2>Комментарии</h2>

            <label v-if="isOwner" class="comments-setting">
              <input
                type="checkbox"
                :checked="trip.allow_comments"
                :disabled="updatingCommentsSettings"
                @change="toggleAllowComments"
              />
              Разрешить комментарии
            </label>

            <CommentList
              :comments="comments"
              :can-delete="canDeleteComment"
              :interactive="auth.isAuthenticated"
              :deleting-id="deletingCommentId"
              :reacting-id="reactingCommentId"
              @delete="removeComment"
              @react="reactToComment"
              @unreact="unreactToComment"
            />

            <ListPager
              :page="commentsPage"
              :total-pages="commentsPagination?.total_pages || 0"
              :total="commentsPagination?.total || 0"
              :disabled="commentsLoading"
              @update:page="commentsPage = $event"
            />

            <CommentForm
              v-if="canComment"
              text-only
              :submitting="submittingComment"
              :error="commentError"
              @submit="submitComment"
            />
          </section>
        </div>
      </div>
    </aside>

    <div v-if="linkedTrack" class="track-map-wrap">
      <TrackMap
        :tracks="[linkedTrack]"
        :selected-track-id="linkedTrack.id"
      />
    </div>
  </div>
</template>

<script setup>
import DOMPurify from 'dompurify'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AuthorLink from '../components/AuthorLink.vue'
import AppBreadcrumbs from '../components/AppBreadcrumbs.vue'
import CommentForm from '../components/CommentForm.vue'
import CommentList from '../components/CommentList.vue'
import FavoriteButton from '../components/FavoriteButton.vue'
import GuestAuthPrompt from '../components/GuestAuthPrompt.vue'
import ListPager from '../components/ListPager.vue'
import ReactionBar from '../components/ReactionBar.vue'
import TrackMap from '../components/TrackMap.vue'
import TripGallery from '../components/TripGallery.vue'
import { favoriteUser, fetchFavoriteUsers, unfavoriteUser } from '../api/favorites'
import {
  createTripComment,
  deleteTrip,
  deleteTripComment,
  favoriteTrip,
  fetchTrip,
  fetchTripComments,
  removeTripCommentReaction,
  removeTripReaction,
  setTripCommentReaction,
  setTripReaction,
  unfavoriteTrip,
  updateTripCommentsSettings,
  updateTripVisibilitySettings,
} from '../api/trips'
import { useAuthStore } from '../stores/auth'
import { formatDistance, formatDuration } from '../utils/trackFormat'
import { trackMapColor } from '../utils/trackMapColors'
import { formatTripDate, visibilityLabel } from '../utils/tripFormat'
import { userDisplayName } from '../utils/userDisplay'
import { parsePaginatedList } from '../utils/pagination'

const PAGE_SIZE = 10

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()

const trip = ref(null)
const comments = ref([])
const commentsPage = ref(1)
const commentsPagination = ref(null)
const commentsLoading = ref(false)
const loading = ref(true)
const error = ref('')

const breadcrumbItems = computed(() => [
  { label: 'Поездки', to: { name: 'trips-home' } },
  { label: trip.value?.name || 'Поездка' },
])

const deleting = ref(false)
const submittingComment = ref(false)
const commentError = ref('')
const deletingCommentId = ref(null)
const reactingCommentId = ref(null)
const reactingTrip = ref(false)
const favoritingTrip = ref(false)
const favoritingAuthor = ref(false)
const authorFavorited = ref(false)
const updatingCommentsSettings = ref(false)
const updatingVisibilitySettings = ref(false)

const isOwner = computed(() => {
  if (!trip.value || !auth.user) return false
  return trip.value.user_id === auth.user.id
})

const canComment = computed(() => {
  if (!trip.value || !auth.user) return false
  return isOwner.value || trip.value.allow_comments
})

const linkedTrack = computed(() => {
  if (!trip.value?.track?.coordinates?.length) return null
  return {
    id: trip.value.track.id,
    name: trip.value.track.name,
    coordinates: trip.value.track.coordinates,
    distance_m: trip.value.track.distance_m,
    duration_s: trip.value.track.duration_s,
    color: trackMapColor(0),
  }
})

const sanitizedBody = computed(() => {
  if (!trip.value?.body) return ''
  return DOMPurify.sanitize(trip.value.body, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'h2', 'h3', 'ul', 'ol', 'li', 'a', 'img'],
    ALLOWED_ATTR: ['href', 'src', 'alt'],
  })
})

function canDeleteComment(comment) {
  if (!trip.value || !auth.user) return false
  return comment.author.id === auth.user.id || isOwner.value
}

async function loadTrip() {
  loading.value = true
  error.value = ''

  try {
    const { data } = await fetchTrip(route.params.id)
    trip.value = data

    if (commentsPage.value !== 1) {
      commentsPage.value = 1
    } else {
      await loadComments()
    }

    await loadAuthorFavoriteState()
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось загрузить поездку'
  } finally {
    loading.value = false
  }
}

async function loadAuthorFavoriteState() {
  if (!auth.isAuthenticated || !trip.value || isOwner.value) {
    authorFavorited.value = false
    return
  }

  const { data } = await fetchFavoriteUsers()
  authorFavorited.value = data.some((author) => author.id === trip.value.user_id)
}

async function loadComments() {
  if (!trip.value) return

  commentsLoading.value = true
  try {
    const response = await fetchTripComments(trip.value.id, {
      page: commentsPage.value,
      perPage: PAGE_SIZE,
    })
    const { items, pagination } = parsePaginatedList(response, 'comments')
    comments.value = items
    commentsPagination.value = pagination
  } finally {
    commentsLoading.value = false
  }
}

function updateCommentsPaginationTotal(total) {
  if (!commentsPagination.value) return

  const totalPages = total === 0 ? 0 : Math.ceil(total / PAGE_SIZE)
  commentsPagination.value = {
    ...commentsPagination.value,
    total,
    total_pages: totalPages,
  }
}

async function removeTrip() {
  if (!trip.value) return

  deleting.value = true
  try {
    await deleteTrip(trip.value.id)
    router.push({ name: 'trips-home' })
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось удалить поездку'
  } finally {
    deleting.value = false
  }
}

async function submitComment(formData, { reset }) {
  if (!trip.value) return

  submittingComment.value = true
  commentError.value = ''

  try {
    const { data } = await createTripComment(trip.value.id, formData)
    const newCount = (trip.value.comments_count ?? 0) + 1
    trip.value = {
      ...trip.value,
      comments_count: newCount,
    }
    const lastPage = Math.max(1, Math.ceil(newCount / PAGE_SIZE))

    if (commentsPage.value >= lastPage) {
      comments.value = [...comments.value, data]
      updateCommentsPaginationTotal(newCount)
    } else {
      commentsPage.value = lastPage
    }
    reset()
  } catch (err) {
    const errors = err.response?.data?.errors
    commentError.value =
      errors?.join(', ') || err.response?.data?.error || 'Не удалось добавить комментарий'
  } finally {
    submittingComment.value = false
  }
}

async function removeComment(comment) {
  if (!trip.value) return

  deletingCommentId.value = comment.id
  try {
    await deleteTripComment(trip.value.id, comment.id)
    const newCount = Math.max((trip.value.comments_count ?? 0) - 1, 0)
    trip.value = {
      ...trip.value,
      comments_count: newCount,
    }
    comments.value = comments.value.filter((item) => item.id !== comment.id)

    if (comments.value.length === 0 && commentsPage.value > 1) {
      commentsPage.value -= 1
    } else {
      updateCommentsPaginationTotal(newCount)
    }
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось удалить комментарий'
  } finally {
    deletingCommentId.value = null
  }
}

async function toggleAllowComments(event) {
  if (!trip.value) return

  updatingCommentsSettings.value = true
  try {
    const { data } = await updateTripCommentsSettings(trip.value.id, event.target.checked)
    trip.value = data
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось обновить настройки'
  } finally {
    updatingCommentsSettings.value = false
  }
}

async function toggleVisibility(isPublic) {
  if (!trip.value || trip.value.public === isPublic) return

  updatingVisibilitySettings.value = true
  try {
    const { data } = await updateTripVisibilitySettings(trip.value.id, isPublic)
    trip.value = data
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось обновить видимость'
  } finally {
    updatingVisibilitySettings.value = false
  }
}

async function reactToTrip(value) {
  if (!trip.value) return

  reactingTrip.value = true
  try {
    const { data } = await setTripReaction(trip.value.id, value)
    trip.value = data
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось поставить реакцию'
  } finally {
    reactingTrip.value = false
  }
}

async function unreactToTrip() {
  if (!trip.value) return

  reactingTrip.value = true
  try {
    await removeTripReaction(trip.value.id)
    const { data } = await fetchTrip(trip.value.id)
    trip.value = data
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось снять реакцию'
  } finally {
    reactingTrip.value = false
  }
}

async function favoriteCurrentTrip() {
  if (!trip.value) return

  favoritingTrip.value = true
  try {
    const { data } = await favoriteTrip(trip.value.id)
    trip.value = data
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось добавить в избранное'
  } finally {
    favoritingTrip.value = false
  }
}

async function unfavoriteCurrentTrip() {
  if (!trip.value) return

  favoritingTrip.value = true
  try {
    await unfavoriteTrip(trip.value.id)
    trip.value = { ...trip.value, favorited: false }
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось убрать из избранного'
  } finally {
    favoritingTrip.value = false
  }
}

async function favoriteAuthor() {
  if (!trip.value) return

  favoritingAuthor.value = true
  try {
    await favoriteUser(trip.value.user_id)
    authorFavorited.value = true
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось добавить автора в избранное'
  } finally {
    favoritingAuthor.value = false
  }
}

async function unfavoriteAuthor() {
  if (!trip.value) return

  favoritingAuthor.value = true
  try {
    await unfavoriteUser(trip.value.user_id)
    authorFavorited.value = false
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось убрать автора из избранного'
  } finally {
    favoritingAuthor.value = false
  }
}

async function reactToComment(comment, value) {
  if (!trip.value) return

  reactingCommentId.value = comment.id
  try {
    const { data } = await setTripCommentReaction(trip.value.id, comment.id, value)
    comments.value = comments.value.map((item) => (item.id === comment.id ? data : item))
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось поставить реакцию'
  } finally {
    reactingCommentId.value = null
  }
}

async function unreactToComment(comment) {
  if (!trip.value) return

  reactingCommentId.value = comment.id
  try {
    await removeTripCommentReaction(trip.value.id, comment.id)
    comments.value = comments.value.map((item) =>
      item.id === comment.id
        ? {
            ...comment,
            user_reaction: null,
            likes_count:
              comment.user_reaction === 'like'
                ? Math.max((comment.likes_count ?? 0) - 1, 0)
                : comment.likes_count,
            dislikes_count:
              comment.user_reaction === 'dislike'
                ? Math.max((comment.dislikes_count ?? 0) - 1, 0)
                : comment.dislikes_count,
          }
        : item
    )
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось снять реакцию'
  } finally {
    reactingCommentId.value = null
  }
}

onMounted(loadTrip)
watch(() => route.params.id, loadTrip)
watch(commentsPage, () => {
  loadComments()
})
</script>

<style scoped>
.track-page-layout {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.track-sidebar {
  width: 100%;
  flex: 1;
  min-height: 0;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.track-page-layout:not(.track-page-layout--no-map) .track-sidebar {
  border-bottom: 1px solid #e2e8f0;
}

.track-title {
  margin: 0 0 0.75rem;
  font-size: 1.25rem;
}

.track-sidebar-content {
  padding: 1rem 1.25rem;
}

.track-sidebar-scroll {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.track-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
}

.track-stats {
  font-size: 0.875rem;
  color: #334155;
}

.visibility-field {
  border: none;
  margin: 0.5rem 0 0;
  padding: 0;
}

.visibility-field legend {
  margin-bottom: 0.375rem;
  padding: 0;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.author-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.25rem;
}

.author-row .track-item-meta {
  margin-top: 0;
}

.comments-section {
  margin-top: 1.5rem;
}

.comments-section h2 {
  margin-top: 0;
}

.comments-setting {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
}

.track-map-wrap {
  position: relative;
  flex-shrink: 0;
  height: 45vh;
  min-height: 240px;
  min-width: 0;
  overflow: hidden;
}

@media (min-width: 768px) {
  .track-page-layout {
    flex-direction: row;
    height: 100%;
  }

  .track-sidebar {
    width: 360px;
    max-width: none;
    max-height: none;
    height: 100%;
    min-height: 0;
    border-bottom: none;
    border-right: 1px solid #e2e8f0;
  }

  .track-map-wrap {
    flex: 1;
    height: auto;
    min-height: 0;
    max-height: none;
  }
}

.trip-companions,
.linked-track {
  margin-top: 1rem;
}

.trip-companions h3,
.linked-track h3 {
  margin: 0 0 0.5rem;
  font-size: 1rem;
}

.companion-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.trip-body :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 0.375rem;
  margin: 0.5rem 0;
}

.trip-body :deep(h2),
.trip-body :deep(h3) {
  margin: 1rem 0 0.5rem;
}
</style>
